import { bytesToNDigits } from '@mail/cross-sizes-utils';
import React, { type ReactNode } from 'react';
import { cloudFilesMigrationUrls } from 'reactApp/appHelpers/featuresHelpers/features/cloudFilesMigrationDesktop';
import { FilesMigrationStatus } from 'reactApp/modules/filesMigration/filesMigration.types';
import type { OpenBuyDisableAdsAction } from 'reactApp/modules/payment/payment.types';
import opener from 'reactApp/utils/opener';

interface FilesMigrationStatusText {
    title: ReactNode;
    description: ReactNode;
}

interface TextsData {
    allChatsCount: number;
    migratedBytes: number;
    migratedChatsCount: number;
}

interface TextsOptions {
    isB2BUser: boolean;
}

export const getTexts = (status: FilesMigrationStatus | null, data: TextsData): FilesMigrationStatusText => {
    let title: ReactNode = '';
    let description: ReactNode = '';
    const downloadStoppedText = 'Загрузка приостановлена';

    if (status === FilesMigrationStatus.waitTakeout || status === FilesMigrationStatus.authed) {
        title = <>Загрузка начнётся автоматически через 24&nbsp;часа</>;
        description = (
            <>
                Нажмите &laquo;Разрешить&raquo; или &laquo;Allow&raquo; в&nbsp;системном чате Telegram, а&nbsp;потом
                &laquo;Это&nbsp;я&raquo; над списком чатов&nbsp;&mdash; и&nbsp;копирование начнётся
            </>
        );
    } else if (status === FilesMigrationStatus.relogin) {
        title = downloadStoppedText;
    } else if (status === FilesMigrationStatus.error || status === FilesMigrationStatus.inProgress) {
        title = 'Загрузка файлов';

        const { migratedBytes, allChatsCount, migratedChatsCount } = data;
        const space = bytesToNDigits(migratedBytes, 3);
        const remainingChats = allChatsCount - migratedChatsCount;
        description = (
            <>
                Сохранили {space.value} из&nbsp;{migratedChatsCount}&nbsp;чатов. Осталось {remainingChats}&nbsp;чатов
            </>
        );
    } else if (status === FilesMigrationStatus.quotaExausted) {
        title = downloadStoppedText;
    } else if (
        status === FilesMigrationStatus.cancelInProgress ||
        status === FilesMigrationStatus.canceled ||
        status === FilesMigrationStatus.declineTakeout
    ) {
        title = downloadStoppedText;
    } else if (status === FilesMigrationStatus.success) {
        title = 'Загрузка завершена';
    }

    return { title, description };
};

interface InfoblockContent {
    type: 'info' | 'success';
    title: ReactNode;
    description: ReactNode;

    bttnText?: ReactNode;
    onClick?: () => void;
}

export const getInfoblockContent = (
    status: FilesMigrationStatus | null,
    onBuyClick: () => { payload: OpenBuyDisableAdsAction; type: string },
    options: TextsOptions
): InfoblockContent | null => {
    const { isB2BUser } = options;

    if (status === FilesMigrationStatus.relogin) {
        const {
            relogin: { url, currentPage },
        } = cloudFilesMigrationUrls;

        return {
            type: 'info',
            title: <>Файлы не&nbsp;загрузились</>,
            description: 'Авторизуйтесь, чтобы продолжить',
            bttnText: 'Авторизоваться снова',
            onClick: () => {
                opener(url, currentPage);
            },
        };
    } else if (status === FilesMigrationStatus.quotaExausted) {
        if (isB2BUser) {
            return {
                type: 'info',
                title: 'Закончилась память',
                description: <>Удалите ненужные файлы или напишите администратору с просьбой увеличить размер хранилища</>,
            };
        }

        return {
            type: 'info',
            title: 'Закончилась память',
            description: <>Увеличьте&nbsp;её, чтобы всё поместилось</>,
            bttnText: <>Увеличить память за&nbsp;1&nbsp;₽</>,
            onClick: () => {
                onBuyClick();
            },
        };
    } else if (status === FilesMigrationStatus.cancelInProgress) {
        return {
            type: 'info',
            title: 'Вы отклонили копирование',
            description: <>Сможете возобновить его чуть позже</>,
        };
    } else if (status === FilesMigrationStatus.canceled || status === FilesMigrationStatus.declineTakeout) {
        const {
            restart: { url, currentPage },
        } = cloudFilesMigrationUrls;

        return {
            type: 'info',
            title: 'Вы отклонили копирование',
            description: <>Если хотите начать заново, нажмите кнопку&nbsp;ниже</>,
            bttnText: 'Начать заново',
            onClick: () => {
                opener(url, currentPage);
            },
        };
    } else if (status === FilesMigrationStatus.success) {
        const {
            restart: { url, currentPage },
        } = cloudFilesMigrationUrls;

        return {
            type: 'success',
            title: 'Загрузка в папку Telegram завершена',
            description: <>Файлы больше не&nbsp;копируются, сессия между Облаком и&nbsp;Telegram полностью завершена</>,
            bttnText: 'Скопировать ещё',
            onClick: () => {
                opener(url, currentPage);
            },
        };
    }
    return null;
};
